import { COMBOBOX_MAX_OPTIONS_SHOW_ALL } from '@src/constants.ts';
import {
  ComboboxAddOption,
  type ComboboxAddOptionProperties,
} from './combobox/ComboboxAddOption';
import { ComboboxBase } from './combobox/ComboboxBase';
import { ComboboxDropDownButton } from './combobox/ComboboxDropDownButton';
import {
  ComboboxError,
  type ComboboxErrorProperties,
} from './combobox/ComboboxError';
import {
  ComboboxInput,
  type ComboboxInputProperties,
} from './combobox/ComboboxInput';
import { ComboboxLeadingIcon } from './combobox/ComboboxLeadingIcon';
import { ComboboxOption } from './combobox/ComboboxOption';
import { ComboboxOptions } from './combobox/ComboboxOptions';
import {
  ComboboxTitle,
  type ComboboxTitleProperties,
} from './combobox/ComboboxTitle';
import { type IconType } from '@src/IconType.ts';
import { MAXIMUM_DROPDOWN_ITEMS } from '@funnellab/shared/src/constants.ts';
import {
  type ValidationMode,
  type ValidationResult,
  type ValidatorFn,
} from '@funnellab/shared/src/validation';
import { useAllOptions } from '@src/hooks/input/combobox/useAllOptions';
import { useArrayState } from 'rooks';
import { useMatchedOptions } from '@src/hooks/input/combobox/useMatchedOptions';
import { useValidation } from '@hooks/useValidation.ts';
import {
  type FC,
  type LegacyRef,
  type PropsWithChildren,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import escapeRegExp from 'lodash.escaperegexp';
import type translationDE from '@funnellab/shared/src/locales/de/translation';

export type ComboboxMultipleProperties<Option> = {
  readonly options?: readonly Option[];
  readonly onChange: (newValue: Option[]) => void;
  readonly displayValue: (value: Option) => string;
  readonly value: Option[];
  readonly filterFn?: (
    options: Option,
    query: string,
    queryRegexCaseInsensitive: RegExp,
    foundOptions: Option[],
  ) => boolean;
  readonly title: React.ReactNode;
  readonly TitleComponent?: FC<PropsWithChildren>;
  readonly infoText?: string;
  readonly required?: boolean;
  /** @deprecated validate the input on your own and pass the result */
  readonly validate?: ValidatorFn<Option[]>;
  /** @deprecated validate the input on your own and pass the result */
  readonly validateInputField?: ValidatorFn;
  readonly inputFieldErrorTranslationKey?: keyof typeof translationDE.errorMessages;
  /** @deprecated validate the input on your own and pass the result */
  readonly errorMessages?: Partial<Record<ValidationResult, string>>;
  readonly classNameContainer?: string;
  readonly classNameInput?: string;
  /** @deprecated use passthrough attribute */
  readonly classNameLabel?: string;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly addOptionLabel?: string;
  readonly addOption?: (label: string) => Option | Promise<Option>;
  readonly placeholder?: string;
  readonly leadingIcon?: IconType | null;
  readonly showDropdownButton?: boolean;
  readonly hideCheckmark?: boolean;
  readonly onOptionsChange?: (options: Option[]) => void;
  readonly onQueryChange?: (query: string) => void;
  readonly sortFn?: (a: Option, b: Option) => number;
  readonly disableSort?: boolean;
  /** @deprecated validate the input on your own and pass the result */
  readonly validationMode?: ValidationMode;
  readonly showError?: boolean;
  readonly maxOptions?: number;
  readonly buttonTestId?: string;
  readonly inputTestId?: string;
  readonly onBlur?: () => void;
  readonly children?: {
    options?: JSX.Element;
    error?: JSX.Element;
    input?: JSX.Element;
    title?: JSX.Element;
    dropDownButton?: JSX.Element;
  };
  readonly passthrough?: {
    error?: Partial<ComboboxErrorProperties<Option>>;
    title?: Partial<ComboboxTitleProperties<Option>>;
    addOption?: Partial<ComboboxAddOptionProperties<Option>>;
    input?: Partial<ComboboxInputProperties<Option>>;
  };
  readonly isOpen?: boolean;
  readonly query?: string;
  readonly showAllOptions?: boolean;
  readonly showSelectedOptions?: boolean;
  readonly getOptionIcon?: (item: Option) => IconType | undefined;
  readonly getAdditionalOptionContent?: (
    item: Option,
  ) => JSX.Element | undefined;
};

export default forwardRef(function ComboboxMultiple<T>(
  {
    displayValue,
    options = [],
    filterFn = (option, _query, regex) => !!regex.exec(displayValue(option)),
    value,
    onChange,
    onOptionsChange,
    onQueryChange,
    title,
    TitleComponent,
    infoText,
    required = false,
    validate: validateFn,
    validateInputField,
    inputFieldErrorTranslationKey,
    validationMode,
    errorMessages,
    classNameContainer,
    classNameInput,
    classNameLabel,
    disabled = false,
    name,
    addOptionLabel,
    addOption,
    placeholder,
    leadingIcon,
    showDropdownButton = false,
    hideCheckmark = false,
    sortFn = (a, b) => displayValue(a).localeCompare(displayValue(b)),
    disableSort,
    showError = true,
    maxOptions = showDropdownButton
      ? COMBOBOX_MAX_OPTIONS_SHOW_ALL
      : MAXIMUM_DROPDOWN_ITEMS,
    buttonTestId,
    inputTestId,
    onBlur,
    children,
    passthrough,
    isOpen: isOpenProp = false,
    query: queryProp = '',
    showAllOptions = false,
    showSelectedOptions = false,
    getOptionIcon,
    getAdditionalOptionContent,
  }: ComboboxMultipleProperties<T>,
  ref: LegacyRef<HTMLInputElement>,
) {
  const [query, setQuery] = useState(queryProp);
  const [failedToAddOption, setFailedToAddOption] = useState(false);
  const [addedOptions, controlAddedOptions] = useArrayState<T>([]);
  const [isOpen, setIsOpen] = useState(isOpenProp);

  useEffect(() => setIsOpen(isOpenProp), [isOpenProp]);
  useEffect(() => setQuery(queryProp), [queryProp]);

  const allOptions = useAllOptions<T>({
    disableSort,
    options,
    addedOptions,
    displayValue,
    sortFn,
  });
  const regex = new RegExp(escapeRegExp(query), 'i');
  const reduceFunction = (acc: T[], option: T) => {
    if (showAllOptions || filterFn(option, query, regex, acc)) {
      acc.push(option);
    }
    return acc;
  };

  const filteredSelectedOptions = value
    .reduce(reduceFunction, [] as T[])
    .slice(0, maxOptions);
  const filteredUnselectedOptions = allOptions
    .filter(
      (option) =>
        !filteredSelectedOptions
          .map(displayValue)
          .includes(displayValue(option)),
    )
    .reduce(reduceFunction, [] as T[])
    .slice(0, maxOptions - filteredSelectedOptions.length);

  const resortedOptions = useMemo(() => {
    let allOptions: T[] = [];
    if (showSelectedOptions) {
      allOptions = [...filteredSelectedOptions];
    }
    allOptions = [...allOptions, ...filteredUnselectedOptions];

    return disableSort ? allOptions : allOptions.sort(sortFn);
  }, [
    disableSort,
    filteredSelectedOptions,
    filteredUnselectedOptions,
    showSelectedOptions,
    sortFn,
  ]);

  const filteredOptions = useMatchedOptions<T>({
    options: resortedOptions,
    displayValue,
    query,
  });

  const {
    isValid,
    isSecondaryValid,
    errorMessage,
    secondaryErrorMessage,
    runValidation,
    validateSecondaryValue: runInputValidation,
  } = useValidation({
    value,
    validateFn,
    secondaryValidateFn: validateInputField,
    required,
    messageKeys: errorMessages,
    validationMode,
  });

  onBlur ??= () => {
    setQuery('');
    setIsOpen(false);
  };

  return (
    <ComboboxBase
      value={value}
      className={classNameContainer}
      disabled={disabled}
      name={name}
      multiple
      onBlur={onBlur}
      onChange={(value) => {
        setQuery('');
        onChange(value);
        runValidation(value);
        setFailedToAddOption(false);
        setIsOpen(false);
      }}
    >
      {{
        title: (
          <ComboboxTitle<T>
            title={title}
            required={required}
            isValid={isValid}
            className={classNameLabel}
            infoText={infoText}
            TitleComponent={TitleComponent}
            {...passthrough?.title}
          />
        ),
        leadingIcon: <ComboboxLeadingIcon<T> leadingIcon={leadingIcon} />,
        input: children?.input ?? (
          <ComboboxInput<T>
            displayValue={displayValue}
            testId={inputTestId}
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            onBlur={onBlur}
            className={classNameInput}
            hasLeadingIcon={!!leadingIcon}
            ref={ref}
            onChange={(event) => {
              const newQuery = (event.target as HTMLInputElement).value;
              runInputValidation(newQuery);
              setQuery(newQuery);
              setIsOpen(newQuery.length > 0);
              onQueryChange?.(newQuery);
            }}
          />
        ),
        dropDownButton: children?.dropDownButton ?? (
          <ComboboxDropDownButton<T>
            showDropdownButton={showDropdownButton}
            buttonTestId={buttonTestId}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        ),
        options: (
          <ComboboxOptions isOpen={isOpen}>
            {{
              options: children?.options ?? (
                <>
                  {filteredOptions.map(({ option, matchedText }, index) => {
                    const icon = getOptionIcon?.(option);
                    const additionalContent =
                      getAdditionalOptionContent?.(option);
                    return (
                      <ComboboxOption<T>
                        option={option}
                        key={`${displayValue(option)}-${index}`}
                        matchedText={matchedText}
                        disabled={disabled}
                        hideCheckmark={hideCheckmark}
                        displayValue={displayValue}
                        searchQuery={query}
                        icon={icon}
                        additionalContent={additionalContent}
                      />
                    );
                  })}
                </>
              ),
              addOption: (
                <ComboboxAddOption<T>
                  disabled={disabled}
                  buttonTestId={buttonTestId}
                  addOption={addOption}
                  addOptionLabel={addOptionLabel}
                  isValid={isSecondaryValid}
                  inputFieldErrorTranslationKey={inputFieldErrorTranslationKey}
                  filteredOptionsLength={filteredOptions.length}
                  inputValue={query}
                  onOptionAdded={(newOption) => {
                    onChange([...value, newOption]);
                    controlAddedOptions.push(newOption);
                    onOptionsChange?.([...options, newOption]);
                  }}
                  setFailedToAddOption={setFailedToAddOption}
                  onOptionAddFinished={() => {
                    setQuery('');
                    setIsOpen(false);
                  }}
                  errorMessage={secondaryErrorMessage}
                  {...passthrough?.addOption}
                />
              ),
            }}
          </ComboboxOptions>
        ),
        error: children?.error ?? (
          <>
            <ComboboxError<T>
              isValid={isValid}
              failedToAddOption={failedToAddOption}
              errorMessage={errorMessage}
              showError={showError}
              {...passthrough?.error}
            />
          </>
        ),
      }}
    </ComboboxBase>
  );
});
